/**
 * ClipboardHistory.ts - Clipboard history management
 * 
 * CHANGE HISTORY:
 * - 251102_1145 [pending] danielbeer: Created clipboard history management
 * 
 * Manages clipboard history with circular buffer and search capabilities.
 */

import type { ClipboardItem, ClipboardHistory as IClipboardHistory } from '../types';

/**
 * Clipboard history manager
 */
export class ClipboardHistory {
  private items: ClipboardItem[] = [];
  private maxSize: number;
  
  constructor(maxSize: number = 10) {
    this.maxSize = maxSize;
  }
  
  /**
   * Add item to history
   */
  add(item: ClipboardItem): void {
    // Add to front
    this.items.unshift(item);
    
    // Trim if over max size
    if (this.items.length > this.maxSize) {
      this.items = this.items.slice(0, this.maxSize);
    }
  }
  
  /**
   * Get all history items
   */
  getAll(): ClipboardItem[] {
    return [...this.items];
  }
  
  /**
   * Get item by index (0 = most recent)
   */
  get(index: number): ClipboardItem | null {
    return this.items[index] || null;
  }
  
  /**
   * Get history size
   */
  size(): number {
    return this.items.length;
  }
  
  /**
   * Clear history
   */
  clear(): void {
    this.items = [];
  }
  
  /**
   * Search history
   */
  search(query: string): ClipboardItem[] {
    const lowerQuery = query.toLowerCase();
    
    return this.items.filter(item => {
      // Search in data if it's a string
      if (typeof item.data === 'string') {
        return item.data.toLowerCase().includes(lowerQuery);
      }
      
      // Search in metadata
      if (item.metadata.source?.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      
      // Search in format
      if (item.format.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      
      return false;
    });
  }
  
  /**
   * Filter history by format
   */
  filterByFormat(format: string): ClipboardItem[] {
    return this.items.filter(item => item.format === format);
  }
  
  /**
   * Filter history by operation
   */
  filterByOperation(operation: 'copy' | 'cut'): ClipboardItem[] {
    return this.items.filter(item => item.operation === operation);
  }
  
  /**
   * Filter history by time range
   */
  filterByTimeRange(startTime: number, endTime: number): ClipboardItem[] {
    return this.items.filter(item => 
      item.timestamp >= startTime && item.timestamp <= endTime
    );
  }
  
  /**
   * Get most recent item
   */
  getMostRecent(): ClipboardItem | null {
    return this.items[0] || null;
  }
  
  /**
   * Remove item by ID
   */
  remove(id: string): boolean {
    const index = this.items.findIndex(item => item.id === id);
    if (index !== -1) {
      this.items.splice(index, 1);
      return true;
    }
    return false;
  }
  
  /**
   * Set max size
   */
  setMaxSize(size: number): void {
    this.maxSize = size;
    
    // Trim if necessary
    if (this.items.length > size) {
      this.items = this.items.slice(0, size);
    }
  }
  
  /**
   * Get max size
   */
  getMaxSize(): number {
    return this.maxSize;
  }
  
  /**
   * Export history
   */
  export(): IClipboardHistory {
    return {
      items: [...this.items],
      maxSize: this.maxSize
    };
  }
  
  /**
   * Import history
   */
  import(data: IClipboardHistory): void {
    this.maxSize = data.maxSize;
    this.items = data.items.slice(0, this.maxSize);
  }
  
  /**
   * Get statistics
   */
  getStats() {
    const formatCounts: Record<string, number> = {};
    const operationCounts = { copy: 0, cut: 0 };
    
    this.items.forEach(item => {
      // Count formats
      formatCounts[item.format] = (formatCounts[item.format] || 0) + 1;
      
      // Count operations
      operationCounts[item.operation]++;
    });
    
    return {
      totalItems: this.items.length,
      maxSize: this.maxSize,
      formatCounts,
      operationCounts,
      oldestTimestamp: this.items[this.items.length - 1]?.timestamp,
      newestTimestamp: this.items[0]?.timestamp
    };
  }
}
